import {Button, Toolbar, ToolbarItem, ToolbarSpacer} from "@progress/kendo-react-buttons"
import {Field, Form, FormClassComponent, FormRenderProps, FormSubmitClickEvent} from "@progress/kendo-react-form"
import {Input, NumericTextBox} from "@progress/kendo-react-inputs"
import dayjs from "dayjs"
import {
    MDBBtn,
    MDBCol,
    MDBIcon,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
} from "mdbreact"
import React, {useEffect} from "react"
import {useDispatch} from "react-redux"
import {setToastr} from "../../containers/App/appSlice"
import {getTestCosts} from "../../containers/DvsaTest/dvsatestsSlice"
import {
    TestPackageDetails,
    deleteTestPackageDetails,
    disableTestPackage,
    getTestPackageBookingImpactGrid,
    postTestPackageDetails,
    setTestPackageDetails,
    showAddEditTestPackageModal,
    toggleImpactModal,
} from "../../containers/Fees/feesSlice"
import {RootState} from "../../rootReducer"
import {SpinnerFragment} from "../../shared/CommonItems"
import {useAppSelector} from "../../store"
import AlertToastr from "../Shared/alertToastr"
import {PermissionsChecker} from "../Shared/permissionChecker"
import TextAreaWithFloatingLabel from "../Shared/textAreaWithFloatingLabel"
import {requiredValidator} from "../Shared/validators"
import DvsaTestCharges from "./dvsaTestCharges"
import {TestPackageEffectiveDatePicker} from "./testPackageEffectiveDatePicker"
import TestPackageImpactModal from "./testPackageImpactModal"

const AddEditTestPackage = (): JSX.Element => {
    const dispatch = useDispatch()

    const [isTestsSelected, setIsTestsSelected] = React.useState<boolean>(true)
    const isAddEditModalToggled = useAppSelector((state: RootState) => state.containers.fees.isAddEditModalToggled)
    const selectedPackageDetails = useAppSelector((state: RootState) => state.containers.fees.testPackageDetails)
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const testPackageTests = useAppSelector((state: RootState) => state.containers.fees.testPackageTests)
    const testPackageDetailsLoading = useAppSelector(
        (state: RootState) => state.containers.fees.testPackageDetailsLoading,
    )
    const testPackageBookingImpactGrid = useAppSelector(
        (state: RootState) => state.containers.fees.testPackageBookingImpactGrid,
    )

    useEffect(() => {
        dispatch(getTestCosts())
    }, [dispatch])

    useEffect(() => {
        testPackageBookingImpactGrid.length > 0 && dispatch(toggleImpactModal(true))
    }, [testPackageBookingImpactGrid])

    useEffect(() => {
        isAddEditModalToggled && formEl.current?.resetForm()
    }, [isAddEditModalToggled])

    useEffect(() => {
        if (testPackageTests.length > 0) setIsTestsSelected(true) // set true if the package has atleast one test
    }, [testPackageTests])

    const formEl = React.createRef<FormClassComponent>()

    const handleSubmit = (e: FormSubmitClickEvent) => {
        setIsTestsSelected(testPackageTests.length !== 0)
        if (e.isValid && testPackageTests.length > 0) {
            let testPackage = {...e.values, dvsaTestCosts: testPackageTests} as TestPackageDetails
            if (
                testPackage.testPackageName.length !== 0 &&
                testPackage.testPackageName.trim().length > 0 &&
                testPackage.effectiveDate !== undefined
            ) {
                let updatedTestPackage = {
                    ...testPackage,
                    dvsaTestCosts: testPackageTests,
                    testPackageName: testPackage.testPackageName.trim(),
                    effectiveDate: dayjs(testPackage.effectiveDate).format("YYYY-MM-DD"),
                    laneFee: testPackage.laneFee == null ? 0 : testPackage.laneFee,
                }
                dispatch(setTestPackageDetails(testPackage))

                dispatch(
                    testPackage.testPackageID === undefined || testPackage.testPackageID === 0
                        ? postTestPackageDetails(selectedDealer?.dealerId ?? 0, updatedTestPackage, false)
                        : getTestPackageBookingImpactGrid(selectedDealer?.dealerId ?? 0, updatedTestPackage),
                )
            } else if (testPackage.testPackageName.trim().length === 0) {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: `Test package name cannot be empty`,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            }
        }
    }

    const checkEffectiveDate = () =>
        selectedPackageDetails.testPackageID !== 0 &&
        selectedPackageDetails.testPackageID !== undefined &&
        dayjs(selectedPackageDetails.effectiveDate).startOf("day").format("DD/MM/YYYY") ===
            dayjs(new Date()).startOf("day").format("DD/MM/YYYY")

    return (
        <>
            <AlertToastr />
            <MDBModal staticBackdrop animationDirection="right" show={isAddEditModalToggled} tabIndex="-1">
                <MDBModalDialog position="top right modal-xl" side>
                    <MDBModalContent className="full-height">
                        <MDBModalHeader>
                            <MDBModalTitle>
                                Test Package {selectedDealer && <>for {selectedDealer.dealerName}</>}
                            </MDBModalTitle>
                            <MDBBtn
                                color="none"
                                className="btn-close"
                                onClick={() => {
                                    dispatch(showAddEditTestPackageModal(false))
                                    dispatch(setTestPackageDetails({} as TestPackageDetails))
                                }}
                            ></MDBBtn>
                        </MDBModalHeader>
                        <PermissionsChecker permission={["TestPackages.Edit"]}>
                            <Toolbar className="shadow-0 border-bottom p-0 pb-1 pt-1">
                                <ToolbarItem>
                                    <Button
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        iconClass="fa fa-save"
                                        onClick={e => formEl.current && formEl.current.onSubmit(e)}
                                        disabled={checkEffectiveDate()}
                                    >
                                        Save
                                    </Button>
                                </ToolbarItem>
                                {selectedPackageDetails.testPackageID && (
                                    <>
                                        <PermissionsChecker permission={["TestPackages.Delete"]}>
                                            {!selectedPackageDetails.hasBookings && (
                                                <Button
                                                    onClick={() =>
                                                        dispatch(
                                                            deleteTestPackageDetails(
                                                                selectedDealer?.dealerId ?? 0,
                                                                selectedPackageDetails.testPackageID,
                                                            ),
                                                        )
                                                    }
                                                    type="button"
                                                    fillMode="flat"
                                                    themeColor={"primary"}
                                                    iconClass="fa fa-trash"
                                                    title="Delete"
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() =>
                                                    dispatch(
                                                        disableTestPackage(
                                                            selectedDealer?.dealerId ?? 0,
                                                            selectedPackageDetails.testPackageID,
                                                            selectedPackageDetails.active ? false : true,
                                                        ),
                                                    )
                                                }
                                                type="button"
                                                fillMode="flat"
                                                themeColor={"primary"}
                                                iconClass={
                                                    selectedPackageDetails.active
                                                        ? "k-icon k-i-cancel"
                                                        : "k-icon k-i-reload"
                                                }
                                                title={selectedPackageDetails.active ? "Disable" : "Enable"}
                                            >
                                                {selectedPackageDetails.active ? "Disable" : "Enable"}
                                            </Button>
                                        </PermissionsChecker>
                                    </>
                                )}
                                <ToolbarSpacer />
                                {selectedPackageDetails.hasBookings && (
                                    <ToolbarItem>
                                        <span>
                                            <MDBIcon
                                                className="me-1"
                                                fas
                                                icon="exclamation-triangle"
                                                size="default"
                                                color="danger"
                                            />
                                            Test Package applied in Bookings, cannot delete
                                        </span>
                                    </ToolbarItem>
                                )}
                            </Toolbar>
                        </PermissionsChecker>
                        {testPackageDetailsLoading && <SpinnerFragment />}
                        <MDBModalBody>
                            <Form
                                ref={formEl}
                                key={JSON.stringify(selectedPackageDetails)}
                                initialValues={selectedPackageDetails ? selectedPackageDetails : {}}
                                onSubmitClick={handleSubmit}
                                render={(formRenderProps: FormRenderProps) => (
                                    <MDBRow>
                                        <MDBCol>
                                            <MDBRow className="mb-3">
                                                <MDBCol md="6">
                                                    <Field
                                                        required
                                                        name="testPackageName"
                                                        id="testPackageName"
                                                        component={Input}
                                                        label={"Test Package Name"}
                                                        validator={requiredValidator}
                                                        disabled={selectedPackageDetails.testPackageID > 0}
                                                    />
                                                </MDBCol>
                                                <MDBCol md="2">
                                                    <Field
                                                        name="laneFee"
                                                        id="laneFee"
                                                        component={NumericTextBox}
                                                        label="Lane Fee"
                                                        min={0}
                                                    />
                                                </MDBCol>
                                                <MDBCol md="2">
                                                    <Field
                                                        name="testFee"
                                                        id="testFee"
                                                        component={NumericTextBox}
                                                        label="Dvsa Test Fee"
                                                        min={0}
                                                    />
                                                </MDBCol>
                                                <MDBCol md="2">
                                                    <Field
                                                        name="duration"
                                                        id="duration"
                                                        component={NumericTextBox}
                                                        label="Duration"
                                                        min={0}
                                                        format="n0"
                                                        parse={(value: string) =>
                                                            parseFloat(value.replace(/[^\d.-]/g, ""))
                                                        }
                                                    />
                                                </MDBCol>
                                            </MDBRow>
                                            <MDBRow className="mb-3">
                                                <MDBCol>
                                                    <Field
                                                        name="testPackageDescription"
                                                        id="testPackageDescription"
                                                        component={Input}
                                                        label="Description"
                                                    />
                                                </MDBCol>
                                                <MDBCol md="3" sm="3">
                                                    <Field
                                                        required
                                                        name={"effectiveDate"}
                                                        component={TestPackageEffectiveDatePicker}
                                                        updateStateValue={(value: Date) => {
                                                            dispatch(
                                                                setTestPackageDetails({
                                                                    ...formEl.current?.values,
                                                                    effectiveDate: new Date(value),
                                                                } as TestPackageDetails),
                                                            )
                                                        }}
                                                        label="Effective Date"
                                                        tabIndex={1}
                                                    />
                                                </MDBCol>
                                            </MDBRow>
                                            <MDBRow className="mb-3">
                                                <MDBCol>
                                                    <Field
                                                        name="notes"
                                                        id="notes"
                                                        component={TextAreaWithFloatingLabel}
                                                        label="Notes"
                                                    />
                                                </MDBCol>
                                            </MDBRow>
                                            <MDBRow>
                                                <MDBCol>
                                                    <DvsaTestCharges isError={!isTestsSelected} />
                                                </MDBCol>
                                            </MDBRow>
                                        </MDBCol>

                                        <div className="clear"></div>
                                    </MDBRow>
                                )}
                            />
                        </MDBModalBody>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            {testPackageBookingImpactGrid.length > 0 && <TestPackageImpactModal />}
        </>
    )
}

export default AddEditTestPackage
